import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type ApiWorkspaceDetailResponse from 'src/core/models/api/responses/ApiWorkspaceDetailResponse';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import { useWorkspacesStore } from 'src/domain/stores/useWorkspacesStore';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const initialState = {
	workspaceDetail: null as ApiWorkspaceDetailResponse | null,
	isLoading: false,
	errorMessage: null as string | null,
};

export const useResortStore = create(
	combine({ ...initialState }, (set, get) => {
		const reset = () => set({ ...initialState });

		const init = () => reset();

		const getWorkspaceDetail = async ({
			workspaceId,
			onSuccess,
			onError,
			onFinally,
		}: {
			workspaceId: number;
			onSuccess?: () => void;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			if (get().workspaceDetail === null) {
				set({ isLoading: true });
			}
			set({ errorMessage: null });

			await useWorkspacesStore.getState().getApiWorkspaceDetail({
				workspaceId,
				onSuccess: (response) => {
					set({ workspaceDetail: response.data });
					onSuccess?.();
				},
				onError: (error) => {
					set({ errorMessage: toErrorMessage(error) });
					reportToCallerOrCrashlytics({ message: 'Fetching the resort failed.', error, onError });
				},
				onFinally: () => {
					set({ isLoading: false });
					onFinally?.();
				},
			});
		};

		return {
			init,
			getWorkspaceDetail,
			reset,
		};
	}),
);
